import { Box, Typography } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import devBadge from "../../../assets/images/svgs/PlaygroundHub/bria-dev-badge.svg";
import { POSTHOG_EVENTS } from "../../../constants/PostHogEvents.ts";
import RouterConstants from "../../../constants/RouterConstants.ts";
import { useAppStore } from "../../../hooks/useStores";
import postHogService from "../../../services/PostHogService";
import { hasOrganization } from "../../../utils";
import BriaButton from "../../common/BriaButton/BriaButton";
import ConfirmationPopup from "../../common/ConfirmationPopup/ConfirmationPopup";
import styles from "./PlaygroundUserToOrgConversionPopup.module.scss";

const PlaygroundUserToOrgConversionPopup = () => {
	const { t } = useTranslation("translation", { keyPrefix: "userToOrgConversionPopup" });
	const { uiStore, playgroundUserToOrgConversionStore } = useAppStore();
	const [showConversionPopup, setShowConversionPopup] = useState(false);
	const isTargetRoute =
		window.location.pathname === RouterConstants.IMAGE_GENERATION.DEMO.IMAGE_GENERATION.fullPath ||
		window.location.pathname === RouterConstants.IMAGE_EDITING.IMAGE.DEMO.fullPath ||
		window.location.pathname === RouterConstants.IMAGE_TO_IMAGE.fullPath ||
		window.location.pathname === RouterConstants.TEXT_TO_IMAGE.fullPath;

	// Exit intent detection (mouse leaves page toward top)
	useEffect(() => {
		if (hasOrganization() || !isTargetRoute) return;

		const onMouseLeave = (e: MouseEvent) => {
			if (e.clientY <= 0 && !playgroundUserToOrgConversionStore.shownAtExitIntent) {
				setShowConversionPopup(true);
				playgroundUserToOrgConversionStore.markExitIntentShown();
			}
		};

		document.addEventListener("mouseleave", onMouseLeave);
		return () => document.removeEventListener("mouseleave", onMouseLeave);
	}, [isTargetRoute]);

	// Generation thresholds logic (10, 50 only once each)
	useEffect(() => {
		if (hasOrganization() || !isTargetRoute) return;
		const count = playgroundUserToOrgConversionStore.generationsCount;
		if (!playgroundUserToOrgConversionStore.shownAt10 && count >= 10) {
			setShowConversionPopup(true);
			playgroundUserToOrgConversionStore.markShownAt(10);
		} else if (!playgroundUserToOrgConversionStore.shownAt50 && count >= 50) {
			setShowConversionPopup(true);
			playgroundUserToOrgConversionStore.markShownAt(50);
		}
	}, [playgroundUserToOrgConversionStore.generationsCount]);

	const handlePrimary = () => {
		postHogService.trackEvent(POSTHOG_EVENTS.PLAYGROUND_USER_TO_ORG_CONVERSION_POPUP);
		uiStore.showDialog("CreateOrganizationDialog");
		setShowConversionPopup(false);
	};

	return (
		<ConfirmationPopup
			open={showConversionPopup && !hasOrganization()}
			onClose={() => setShowConversionPopup(false)}
			hideCancel={true}
			hideActions={true}
		>
			<Box className={styles.container}>
				<Box className={styles.iconHalo}>
					<Box className={styles.iconCircle}>
						<img className={styles.icon} src={devBadge} alt="dev badge" />
					</Box>
				</Box>
				<Typography className={styles.title}>{t("title")}</Typography>
				<Typography className={styles.subtitle}>{t("subTitle")}</Typography>
				<Box className={styles.bodyContainer}>
					<Typography className={styles.body}>
						{t("body")}
						<Typography component={"span"} className={`${styles.body} ${styles.highlight}`}>
							{t("body2")}
						</Typography>
						<Typography component={"span"} className={styles.body}>
							{t("body3")}
						</Typography>
					</Typography>
				</Box>

				<Box className={styles.actions}>
					<BriaButton onClick={handlePrimary} buttonType="primaryMedium">
						{t("cta")}
					</BriaButton>
				</Box>
			</Box>
		</ConfirmationPopup>
	);
};

export default observer(PlaygroundUserToOrgConversionPopup);
